import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Select } from "../design/select";
import { Textarea } from "../design/textarea";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useChildSupportPayment } from "../contexts/ChildSupportPaymentContext";
import { useChildSupportOrder } from "../contexts/ChildSupportOrderContext";
import { ChildSupportPayment } from "../models/ChildSupportPayment";
import { ChildSupportOrder } from "../models/ChildSupportOrder";
import { Checkbox, CheckboxField } from "../design/checkbox";

interface Props {
  className?: string;
  children?: React.ReactNode;
  childSupportOrderId: ChildSupportOrder["_id"];
  childSupportPayment?: ChildSupportPayment;
  onChildSupportPaymentCreated?: (paymentId: string) => void;
}

interface Errors {
  amount?: string;
  date?: string;
}

const ChildSupportPaymentButton: React.FC<Props> = ({
  className,
  children,
  childSupportOrderId,
  childSupportPayment,
  onChildSupportPaymentCreated,
}) => {
  const { currentChildSupportOrder } = useChildSupportOrder();
  const { createChildSupportPayment, updateChildSupportPayment } =
    useChildSupportPayment();

  const [isOpen, setIsOpen] = useState(false);

  const [amount, setAmount] = useState<number>(
    currentChildSupportOrder?.amount ?? 850,
  );
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [status, setStatus] = useState<ChildSupportPayment["status"]>("paid");
  const [method, setMethod] = useState<string>("Zelle");
  const [notes, setNotes] = useState<string>("");

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  useEffect(() => {
    if (childSupportPayment && isOpen) {
      setAmount(childSupportPayment.amount ?? 850);
      setDate(
        childSupportPayment.date
          ? new Date(childSupportPayment.date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      );
      setStatus(childSupportPayment.status ?? "paid");
      setMethod(childSupportPayment.method ?? "Zelle");
      setNotes(childSupportPayment.notes ?? "");
    }
  }, [childSupportPayment, isOpen]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!amount) newErrors.amount = "Amount is required.";
    if (!date) newErrors.date = "Date is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = (blockClose = false) => {
    if (!childSupportPayment) {
      setAmount(currentChildSupportOrder?.amount ?? 850);
      setDate(new Date().toISOString().split("T")[0]);
      setStatus("paid");
      setMethod("Zelle");
      setNotes("");
    }
    if (!blockClose) setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);

    try {
      if (childSupportPayment) {
        await updateChildSupportPayment(childSupportPayment._id, {
          amount,
          date: new Date(date),
          status,
          method,
          notes,
          childSupportOrder: childSupportOrderId,
        });
      } else {
        const paymentId = await createChildSupportPayment({
          amount,
          date: new Date(date),
          status,
          method,
          notes,
          childSupportOrder: childSupportOrderId,
        });
        if (onChildSupportPaymentCreated) {
          onChildSupportPaymentCreated(paymentId);
        }
      }
      clearForm(isCreatingMore);
    } catch (err) {
      console.error("Failed to submit child support payment", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!childSupportPayment && <PlusIcon className="w-4 h-4" />}
            <span className="hidden sm:block">
              {childSupportPayment ? "Edit Payment" : "Add Payment"}
            </span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {childSupportPayment
            ? "Edit Child Support Payment"
            : "Create Child Support Payment"}
        </DialogTitle>
        <DialogBody>
          {childSupportOrderId}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
                {errors.date && <ErrorMessage>{errors.date}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Amount</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  placeholder="Payment Amount"
                />
                {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Status</Label>
                <Select
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as ChildSupportPayment["status"])
                  }
                >
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                </Select>
              </Field>

              <Field>
                <Label>Method</Label>
                <Input
                  type="text"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  placeholder="Zelle, Cash, Bank Transfer, etc."
                />
              </Field>

              <Field>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes..."
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>

        <DialogActions className="justify-between">
          {!childSupportPayment && (
            <CheckboxField>
              <Checkbox
                checked={isCreatingMore}
                onChange={(checked) => setIsCreatingMore(checked)}
              />
              <Label>Create more</Label>
            </CheckboxField>
          )}
          <div className="flex gap-2">
            <Button plain onClick={() => clearForm(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChildSupportPaymentButton;
